import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {number | null}
   */
  #overflowDeactivateTimer = null;

  /**
   * @type {number}
   */
  #minimumTouchTarget = 44;

  /**
   * @type {number | null}
   */
  #lastHoverBridgeHeight = null;

  /**
   * @type {(event: Event) => void}
   */
  #overflowPointerLeaveListener = () => this.#deactivate();

  connectedCallback() {
    super.connectedCallback();

    if (!this.isClickInteractionMode) {
      this.overflowMenu?.addEventListener('pointerleave', this.#overflowPointerLeaveListener);
    }
    this.addEventListener('click', this.#handleClickInteraction);
    this.addEventListener('keydown', this.#handleKeyInteraction);
    document.addEventListener('click', this.#handleOutsideClick);
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();
    this.#readMinimumTouchTarget();
    this.#syncHoverBridgeHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowPointerLeaveListener);
    this.removeEventListener('click', this.#handleClickInteraction);
    this.removeEventListener('keydown', this.#handleKeyInteraction);
    document.removeEventListener('click', this.#handleOutsideClick);
    window.removeEventListener('resize', this.#resizeListener);
    this.#clearOverflowDeactivateTimer();
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    this.#readMinimumTouchTarget();
    this.#syncHoverBridgeHeight();
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get isShowAllMenu() {
    return this.querySelector('.menu-list--show-all') instanceof HTMLElement;
  }

  get isClickInteractionMode() {
    return this.dataset.submenuTrigger === 'click';
  }

  #clearOverflowDeactivateTimer = () => {
    if (this.#overflowDeactivateTimer) {
      clearTimeout(this.#overflowDeactivateTimer);
      this.#overflowDeactivateTimer = null;
    }
  };

  #scheduleOverflowDeactivate = () => {
    this.#clearOverflowDeactivateTimer();
    this.#overflowDeactivateTimer = setTimeout(() => {
      this.#deactivate();
    }, 140);
  };

  #readMinimumTouchTarget = () => {
    const minimumTouchTarget = Number.parseFloat(getComputedStyle(this).getPropertyValue('--minimum-touch-target'));
    this.#minimumTouchTarget = Number.isFinite(minimumTouchTarget) && minimumTouchTarget > 0 ? minimumTouchTarget : 44;
  };

  #syncHoverBridgeHeight = () => {
    const headerHeight = this.headerComponent?.offsetHeight ?? 0;
    const bridgeHeight = Math.max(50, headerHeight - this.#minimumTouchTarget);
    if (bridgeHeight === this.#lastHoverBridgeHeight) return;
    this.#lastHoverBridgeHeight = bridgeHeight;
    this.style.setProperty('--menu-hover-bridge-height', `${bridgeHeight}px`);
  };

  /**
   * Whether movement is from the active item into a lower wrapped row.
   * @param {HTMLElement | null} nextItem
   * @returns {boolean}
   */
  #isLowerWrappedRow(nextItem) {
    if (!this.isShowAllMenu) return false;
    if (!(nextItem instanceof HTMLElement)) return false;
    if (!(this.#state.activeItem instanceof HTMLElement)) return false;
    if (nextItem === this.#state.activeItem) return false;

    const activeRow = this.#state.activeItem.closest('.menu-list__list-item');
    const nextRow = nextItem.closest('.menu-list__list-item');
    if (!(activeRow instanceof HTMLElement) || !(nextRow instanceof HTMLElement)) return false;
    if (activeRow === nextRow) return false;

    const activeRect = activeRow.getBoundingClientRect();
    const nextRect = nextRow.getBoundingClientRect();
    const verticalDelta = nextRect.top - activeRect.top;
    const rowTolerance = 4;

    // Calculate dynamic downward span from actual header height so stores with
    // larger logos/headers are handled without fixed spacing assumptions.
    const headerHeight = this.headerComponent?.offsetHeight ?? 0;
    const maxDownwardSpan = headerHeight > 0 ? headerHeight : activeRect.height * 2;
    if (verticalDelta <= rowTolerance || verticalDelta > maxDownwardSpan + rowTolerance) return false;

    // Preserve open state only when moving mostly downward, not laterally.
    const overlapWidth = Math.min(activeRect.right, nextRect.right) - Math.max(activeRect.left, nextRect.left);
    const minOverlap = Math.min(activeRect.width, nextRect.width) * 0.35;
    return overlapWidth >= minOverlap;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | KeyboardEvent | Event} event
   */
  activate = (event) => {
    if (
      this.isClickInteractionMode &&
      event.type !== 'click' &&
      event.type !== 'keydown' &&
      event.type !== 'pointerdown'
    ) {
      return;
    }

    this.#syncHoverBridgeHeight();
    this.#clearOverflowDeactivateTimer();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const slotOwner = item.closest('.menu-list__list-item');
    const isDefaultSlot = ((slotOwner instanceof HTMLElement ? slotOwner.slot : '') ?? '') === '';
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Wrapped rows: keep current dropdown when crossing downward over non-dropdown items.
    // Same-row lateral movement over non-dropdown items closes immediately.
    if (this.isShowAllMenu && isDefaultSlot && !submenu) {
      if (this.#isLowerWrappedRow(item)) return;
      this.#deactivate(undefined, true);
      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent | Event} event
   */
  deactivate(event) {
    if (this.isClickInteractionMode) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      (event.type === 'blur' || event.type === 'pointerleave') &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');
    const isMovingWithinHeaderMenu =
      event.type === 'pointerleave' && event.relatedTarget instanceof Node && this.contains(event.relatedTarget);
    const nextItem = event.relatedTarget instanceof Element ? findMenuItem(event.relatedTarget) : null;
    const isMovingDownwardFromMoreTrigger = this.#isMovingDownwardFromMoreTrigger(event);

    // Movement rules:
    // - keep open when moving down into the same dropdown path
    // - close when moving sideways to sibling menu items
    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;
    if (!this.isShowAllMenu && isMovingDownwardFromMoreTrigger) {
      this.#scheduleOverflowDeactivate();
      return;
    }
    if (this.isShowAllMenu && isMovingWithinHeaderMenu) {
      if (this.#isLowerWrappedRow(nextItem)) return;
      this.#deactivate(undefined, true);
      return;
    }

    this.#deactivate();
  }

  /**
   * Handle click-to-open desktop interaction mode for menu items with submenus.
   * First click opens submenu, second click on same trigger closes it.
   * @param {MouseEvent} event
   */
  #handleClickInteraction = (event) => {
    if (!this.isClickInteractionMode) return;
    if (!(event.target instanceof Element)) return;

    // Only toggle from top-level triggers; allow submenu links to navigate naturally.
    const trigger = event.target.closest('[ref="menuitem"], .menu-list__list-item[slot="more"] > .menu-list__link');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    const item = findMenuItem(trigger);
    if (!(item instanceof HTMLElement)) return;

    const slotOwner = item.closest('.menu-list__list-item');
    const isDefaultSlot = ((slotOwner instanceof HTMLElement ? slotOwner.slot : '') ?? '') === '';
    const submenu = findSubmenu(item);

    // Let direct links navigate normally when they don't open a submenu.
    if (!submenu && isDefaultSlot) return;

    const isSameItem = item === this.#state.activeItem;

    // First click opens.
    if (!isSameItem) {
      event.preventDefault();
      this.activate({ target: item, type: 'click' });
      return;
    }

    // Second click closes the currently open submenu.
    event.preventDefault();
    this.#deactivate(undefined, true);
  };

  /**
   * Handle keyboard behavior for click interaction mode.
   * @param {KeyboardEvent} event
   */
  #handleKeyInteraction = (event) => {
    if (!this.isClickInteractionMode) return;

    if (event.key === 'Escape') {
      if (this.#state.activeItem) {
        this.#deactivate(undefined, true);
      }
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;

    // Only toggle from top-level triggers; allow submenu links to navigate naturally.
    const trigger = event.target.closest('[ref="menuitem"], .menu-list__list-item[slot="more"] > .menu-list__link');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    const item = findMenuItem(trigger);
    if (!(item instanceof HTMLElement)) return;

    const slotOwner = item.closest('.menu-list__list-item');
    const isDefaultSlot = ((slotOwner instanceof HTMLElement ? slotOwner.slot : '') ?? '') === '';
    const submenu = findSubmenu(item);
    if (!submenu && isDefaultSlot) return;

    // Space should always toggle/open instead of scrolling.
    if (event.key === ' ') {
      event.preventDefault();
      if (item === this.#state.activeItem) {
        this.#deactivate(undefined, true);
      } else {
        this.activate({ target: item, type: 'keydown' });
      }
      return;
    }

    // Enter toggles open/close in click mode.
    event.preventDefault();
    if (item === this.#state.activeItem) {
      this.#deactivate(undefined, true);
    } else {
      this.activate({ target: item, type: 'keydown' });
    }
  };

  /**
   * Close open menu when clicking outside header-menu in click interaction mode.
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (!this.isClickInteractionMode) return;
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate(undefined, true);
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [forceClose]
   */
  #deactivate = (item = this.#state.activeItem, forceClose = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!forceClose && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;
    this.#clearOverflowDeactivateTimer();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * In non-wrapped mode, allow a downward mouse move from "More" toward overflow
   * before closing, using actual header height as the allowable travel span.
   * @param {PointerEvent | FocusEvent} event
   * @returns {boolean}
   */
  #isMovingDownwardFromMoreTrigger(event) {
    if (event.type !== 'pointerleave') return false;
    if (!('clientX' in event) || !('clientY' in event)) return false;
    if (!(this.#state.activeItem instanceof HTMLElement)) return false;

    const activeRow = this.#state.activeItem.closest('.menu-list__list-item');
    if (!(activeRow instanceof HTMLElement) || activeRow.slot !== 'overflow') return false;

    const moreTrigger = this.querySelector('.menu-list__list-item[slot="more"] > .menu-list__link');
    if (!(moreTrigger instanceof HTMLElement)) return false;

    const triggerRect = moreTrigger.getBoundingClientRect();
    const headerHeight = this.headerComponent?.offsetHeight ?? triggerRect.height * 2;
    const maxY = triggerRect.bottom + headerHeight;
    const horizontalTolerance = 8;
    const withinX =
      event.clientX >= triggerRect.left - horizontalTolerance && event.clientX <= triggerRect.right + horizontalTolerance;
    const downward = event.clientY >= triggerRect.bottom - 1;

    return withinX && downward && event.clientY <= maxY;
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const submenuHeights = Array.from(submenus)
      .filter((submenu) => submenu instanceof HTMLElement)
      .map((submenu) => submenu.offsetHeight);
    const maxHeight = submenuHeights.length > 0 ? Math.max(...submenuHeights) : 0;
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  const menuitem = element.matches('[ref="menuitem"]')
    ? element
    : element.closest('[ref="menuitem"]') || element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
